import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { QLINE_TYPE_META, TypeChip, FilterPill } from "../helpers/costCategories"
import { formatMoney } from "../helpers/poTotals"
import ProductSearch from "./ProductSearch"

const GRID = "grid grid-cols-[32px_minmax(0,1fr)_150px_104px_110px_120px] gap-3 items-center"
const t = (key, opts) => I18n.t(`projects.engagements.form.breakdowns.${key}`, opts)

const CELL = "h-8 px-2 text-right tabular-nums rounded-md border border-slate-200 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"

const SubstitutionRow = ({ breakdown, entry, currency, productSearchUrl, onUpdate }) => {
  const [searching, setSearching] = useState(false)
  const quotedUnit = (breakdown.unit_price?.cents || 0) / 100

  const substitute = product => {
    onUpdate(breakdown.id, {
      product_id:  product.id,
      description: product.designation || product.name,
      unit:        product.unit || breakdown.unit,
      unit_price:  (product.purchase_price?.cents || 0) / 100,
      substitute:  { name: product.name },
    })
    setSearching(false)
  }

  const revert = () => onUpdate(breakdown.id, {
    product_id: null, description: null, unit: breakdown.unit, unit_price: quotedUnit, substitute: null,
  })

  if (entry.substitute) {
    const gap = (parseFloat(entry.unit_price) || 0) - quotedUnit
    return (
      <div className="col-span-6 -mt-1 ml-[44px] flex items-center gap-3 text-[11.5px]">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200 font-medium">
          <FontAwesomeIcon icon="right-left" className="w-2.5 h-2.5" />
          {t("substituted_with", { label: breakdown.label || breakdown.qline_label })}
        </span>
        <span className={`tabular-nums ${gap > 0 ? "text-rose-600" : gap < 0 ? "text-emerald-600" : "text-slate-500"}`}>
          {t("substitution_gap", { amount: `${gap > 0 ? "+" : ""}${formatMoney(gap, currency)}` })}
        </span>
        <button type="button" onClick={revert} className="text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline">{t("keep_quoted")}</button>
      </div>
    )
  }

  return (
    <div className="col-span-6 -mt-1 ml-[44px] text-[11.5px]">
      {searching ? (
        <div className="max-w-md">
          <ProductSearch url={productSearchUrl} placeholder={t("search_product")} onSelect={substitute} autoFocus />
        </div>
      ) : (
        <button type="button" onClick={() => setSearching(true)} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900">
          <FontAwesomeIcon icon="right-left" className="w-2.5 h-2.5" />{t("substitute")}
        </button>
      )}
    </div>
  )
}

const BreakdownSelector = ({ breakdowns, selection, currency, loading, onToggle, onUpdate, onSelectMany, productSearchUrl }) => {
  const [activeType, setActiveType] = useState("all")
  const [query, setQuery]           = useState("")

  const counts = useMemo(() => {
    const tally = { all: breakdowns.length }
    breakdowns.forEach((breakdown) => { tally[breakdown.cost_category] = (tally[breakdown.cost_category] || 0) + 1 })
    return tally
  }, [breakdowns])

  const filtered = useMemo(() => {
    let list = activeType === "all" ? breakdowns : breakdowns.filter((breakdown) => breakdown.cost_category === activeType)
    if (query) {
      const needle = query.toLowerCase()
      list = list.filter((breakdown) =>
        (breakdown.label || "").toLowerCase().includes(needle) ||
        (breakdown.qline_label || "").toLowerCase().includes(needle)
      )
    }
    return list
  }, [breakdowns, activeType, query])

  const selectableVisible  = filtered.filter((breakdown) => !breakdown.fully_ordered)
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((breakdown) => selection[breakdown.id])
  const selectedCount      = Object.keys(selection).length

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold text-slate-800">{t("title")}</h3>
        {selectedCount > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[11.5px] font-medium ring-1 ring-inset ring-brand-200">
            <FontAwesomeIcon icon="check" className="w-2.5 h-2.5" />{t("selected_count", { count: selectedCount })}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-400"><FontAwesomeIcon icon="circle-notch" spin className="text-xl" /></div>
      ) : breakdowns.length === 0 ? (
        <div className="px-6 py-10 text-center text-[13px] text-slate-500 border border-dashed border-slate-200 rounded-xl">{t("empty")}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-3 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <FilterPill active={activeType === "all"} onClick={() => setActiveType("all")} label={t("filter_all")} count={counts.all} />
            {Object.keys(QLINE_TYPE_META).map((type) => (
              counts[type] ? (
                <FilterPill key={type} active={activeType === type} onClick={() => setActiveType(type)} label={QLINE_TYPE_META[type].label} count={counts[type]} />
              ) : null
            ))}
            <span className="flex-1" />
            <div className="relative">
              <FontAwesomeIcon icon="magnifying-glass" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("search")}
                className="pl-7 pr-3 py-1 bg-slate-50 rounded-md text-[12.5px] outline-none focus:ring-2 focus:ring-brand-500/30 w-48"
              />
            </div>
          </div>

          <div className={`${GRID} px-3 py-2 bg-slate-50/70 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
            <div className="flex items-center justify-center">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={allVisibleSelected}
                disabled={selectableVisible.length === 0}
                onChange={() => onSelectMany(selectableVisible, !allVisibleSelected)}
                title={t("select_all")}
              />
            </div>
            <div>{t("designation")}</div>
            <div>{t("category")}</div>
            <div className="text-right">{t("quantity")}</div>
            <div className="text-right">{t("unit_price")}</div>
            <div className="text-right">{t("line_total")}</div>
          </div>

          <div className="max-h-[42vh] overflow-y-auto divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <div className="px-6 py-8 text-center text-[13px] text-slate-400">{t("no_match")}</div>
            ) : filtered.map((breakdown) => {
              const entry      = selection[breakdown.id]
              const selected   = !!entry
              const quantity   = selected ? entry.quantity : breakdown.remaining_quantity
              const unitPrice  = selected ? (parseFloat(entry.unit_price) || 0) : (breakdown.unit_price?.cents || 0) / 100
              const lineTotal  = (parseFloat(quantity) || 0) * unitPrice
              const ordered    = breakdown.ordered_quantity > 0
              const budgetLeft = (breakdown.remaining_budget?.cents || 0) / 100
              const label      = (selected && entry.description) || breakdown.label || breakdown.qline_label || "—"
              const canSubstitute = selected && productSearchUrl && breakdown.cost_category !== "labor"
              return (
                <div
                  key={breakdown.id}
                  className={`${GRID} px-3 py-2.5 text-[12.5px] ${breakdown.fully_ordered ? "opacity-50 bg-slate-50/40" : selected ? "bg-brand-50/30" : "hover:bg-slate-50/60"}`}
                >
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50 cursor-pointer"
                      checked={selected}
                      disabled={breakdown.fully_ordered}
                      onChange={() => onToggle(breakdown)}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-slate-900 truncate" title={label}>{label}</div>
                    <div className="text-[11px] text-slate-400 truncate flex items-center gap-1.5">
                      {breakdown.label && breakdown.qline_label && <span className="truncate">{breakdown.qline_label}</span>}
                      {breakdown.fully_ordered ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 shrink-0"><FontAwesomeIcon icon="check" className="w-2.5 h-2.5" />{t("fully_ordered")}</span>
                      ) : ordered && breakdown.remaining_quantity <= 0 ? (
                        <span className="text-amber-600 shrink-0">{t("budget_remaining", { amount: formatMoney(budgetLeft, currency) })}</span>
                      ) : ordered ? (
                        <span className="text-amber-600 shrink-0">{t("already_ordered", { ordered: breakdown.ordered_quantity, quoted: breakdown.quoted_quantity })}</span>
                      ) : null}
                    </div>
                  </div>
                  <div><TypeChip type={breakdown.cost_category} /></div>
                  <div className="flex items-center justify-end gap-1.5">
                    {selected ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={quantity}
                        onChange={(event) => onUpdate(breakdown.id, { quantity: event.target.value })}
                        className={`w-16 ${CELL}`}
                      />
                    ) : (
                      <span className="tabular-nums text-slate-500">{breakdown.remaining_quantity}</span>
                    )}
                    {(selected ? entry.unit || breakdown.unit : breakdown.unit) && (
                      <span className="text-slate-400 text-[11px] w-6 truncate">{selected ? (entry.unit || breakdown.unit) : breakdown.unit}</span>
                    )}
                  </div>
                  <div className="text-right">
                    {selected ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={entry.unit_price}
                        onChange={(event) => onUpdate(breakdown.id, { unit_price: event.target.value })}
                        className={`w-24 ${CELL}`}
                      />
                    ) : (
                      <span className="tabular-nums text-slate-600">{formatMoney(unitPrice, currency)}</span>
                    )}
                  </div>
                  <div className={`text-right tabular-nums font-medium ${selected ? "text-slate-900" : "text-slate-300"}`}>{formatMoney(lineTotal, currency)}</div>

                  {canSubstitute && (
                    <SubstitutionRow
                      breakdown        = {breakdown}
                      entry            = {entry}
                      currency         = {currency}
                      productSearchUrl = {productSearchUrl}
                      onUpdate         = {onUpdate}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}

export default BreakdownSelector
